import { rest } from 'msw'
import type {
  Contact,
  ContactInstrument,
  ContactPaymentInstruments,
  PostContactCase,
} from 'types/contacts'
import { ContactEntity, InstrumentType, RelationshipType } from 'types/contacts'
import {
  MOCK_CRYPTO_PAYMENT_INSTRUMENT_BTC,
  MOCK_CRYPTO_PAYMENT_INSTRUMENT_ETH,
} from '../../../__mocks__/Contacts/contactPaymentInstruments'
import mockContacts from '../../../__mocks__/Contacts/contacts.json'

const API_URL = process.env.API_URL ?? ''

export const CONTACT_INSTRUMENTS_URL = `${API_URL}/platform/v1/contacts/:contactId/instruments`
export const PAYMENT_INSTRUMENTS_URL = `${API_URL}/platform/v1/payment-instruments`

export const MOCK_CONTACT_ID = '123'

export const MOCK_CONTACT: Contact = {
  id: MOCK_CONTACT_ID,
  name: 'Test Contact',
  defaultReference: '1234567890',
  updatedAt: new Date(),
  createdAt: new Date(),
  relationshipType: RelationshipType.THIRD_PARTY,
  entity: {
    type: ContactEntity.INDIVIDUAL,
    firstName: 'Test',
    lastName: 'Contact',
    dateOfBirth: '1990-01-01',
    address: {
      addressLine1: '123 Main St',
      city: 'Anytown',
      region: 'CA',
      postCode: '12345',
      country: 'US',
    },
  },
  description: 'Test Contact',
}

export const MOCK_BANK_ACCOUNT: ContactInstrument = {
  id: 'instr-1',
  type: InstrumentType.BANK_ACCOUNT,
  currency: 'EUR',
  description: 'Main Bank Account',
  accountNumber: '1234567890',
  accountType: 'Checking',
  paymentMethods: ['SEPA'],
  bank: {
    name: 'Test Bank',
    address: {
      addressLine1: '123 Main St',
      city: 'Anytown',
      region: 'CA',
      postCode: '12345',
      country: 'US',
    },
  },
}

export const MOCK_CRYPTO_INSTRUMENT: ContactInstrument = {
  id: 'instr-2',
  type: InstrumentType.CRYPTO,
  currency: 'ETH',
  description: 'Ethereum Address',
  address: '0xtest456',
}

export const MOCK_CRYPTO_INSTRUMENT_BTC: ContactInstrument = {
  id: 'instr-3',
  type: InstrumentType.CRYPTO,
  currency: 'BTC',
  description: 'Bitcoin Address',
  address: '0x1234567890abcdef',
  network: 'Bitcoin',
}

export const MOCK_INSTRUMENTS = [MOCK_BANK_ACCOUNT, MOCK_CRYPTO_INSTRUMENT]

export const MOCK_ASSIGN_CONTACT: PostContactCase = {
  contactId: '1234',
  externalId: '5678',
}

export const createInstrumentsResponse = (instruments: ContactInstrument[] = MOCK_INSTRUMENTS) => ({
  content: instruments,
  size: 10,
  number: 0,
  totalElements: instruments.length,
})

export const getContacts = rest.get(`${API_URL}/platform/v1/contacts`, async (req, res, ctx) => {
  return res(ctx.json(mockContacts))
})

export const postAssignContactHandler = () =>
  rest.post(
    `${API_URL}/payments/v1/travel-rule-rfis/:externalId/assign-contact`,
    (req, res, ctx) => {
      return res(ctx.json(MOCK_ASSIGN_CONTACT))
    }
  )

export const postAssignContactErrorHandler = () =>
  rest.post(
    `${API_URL}/payments/v1/travel-rule-rfis/:externalId/assign-contact`,
    (req, res, ctx) => {
      return res(ctx.status(500), ctx.json({ message: 'Internal server error' }))
    }
  )

export const getContactsHandler = () =>
  rest.get(`${API_URL}/platform/v1/contacts`, (req, res, ctx) => {
    return res(ctx.json(mockContacts))
  })

export const getContactsEmptyHandler = () =>
  rest.get(`${API_URL}/platform/v1/contacts`, (req, res, ctx) => {
    return res(
      ctx.json({
        content: [],
        pageable: mockContacts.pageable,
        last: true,
        totalPages: 0,
        totalElements: 0,
        first: true,
        size: 10,
        number: 0,
        sort: mockContacts.sort,
        numberOfElements: 0,
        empty: true,
      })
    )
  })

export const getContactsErrorHandler = () =>
  rest.get(`${API_URL}/platform/v1/contacts`, (req, res, ctx) => {
    return res(ctx.status(500))
  })

export const getContactsSearchableHandler = () =>
  rest.get(`${API_URL}/platform/v1/contacts`, (req, res, ctx) => {
    const query = req.url.searchParams.get('q')
    const filtered = query
      ? mockContacts.content.filter(
          contact =>
            contact.name.toLowerCase().includes(query.toLowerCase()) ||
            contact.description?.toLowerCase().includes(query.toLowerCase())
        )
      : mockContacts.content
    return res(
      ctx.json({
        ...mockContacts,
        content: filtered,
        totalElements: filtered.length,
        numberOfElements: filtered.length,
      })
    )
  })

export const getContactsPaginatedHandler = (pageSize = 10) =>
  rest.get(`${API_URL}/platform/v1/contacts`, (req, res, ctx) => {
    const page = Number(req.url.searchParams.get('page')) || 0
    const size = pageSize

    // Split contacts into pages
    const startIndex = page * size
    const endIndex = startIndex + size
    const pageContacts = mockContacts.content.slice(startIndex, endIndex)
    const totalPages = Math.ceil(mockContacts.content.length / size)

    return res(
      ctx.json({
        content: pageContacts,
        pageable: {
          pageNumber: page,
          pageSize: size,
          sort: mockContacts.pageable.sort,
          offset: startIndex,
          paged: true,
          unpaged: false,
        },
        last: page >= totalPages - 1,
        totalPages,
        totalElements: mockContacts.content.length,
        first: page === 0,
        size,
        number: page,
        sort: mockContacts.sort,
        numberOfElements: pageContacts.length,
        empty: pageContacts.length === 0,
      })
    )
  })

export const getContactHandler = (contact: Contact = MOCK_CONTACT) =>
  rest.get(`${API_URL}/platform/v1/contacts/:contactId`, (req, res, ctx) => {
    return res(ctx.json(contact))
  })

export const getContactInstrumentsHandler = (instruments: ContactInstrument[] = MOCK_INSTRUMENTS) =>
  rest.get(CONTACT_INSTRUMENTS_URL, (req, res, ctx) => {
    return res(ctx.json(createInstrumentsResponse(instruments)))
  })

export const getContactErrorHandler = () =>
  rest.get(`${API_URL}/platform/v1/contacts/:contactId`, (req, res, ctx) => {
    return res(ctx.status(500))
  })

export const getContactInstrumentsErrorHandler = () =>
  rest.get(CONTACT_INSTRUMENTS_URL, (req, res, ctx) => {
    return res(ctx.status(500))
  })

export const getContactInstrumentsEmptyHandler = () =>
  rest.get(CONTACT_INSTRUMENTS_URL, (req, res, ctx) => {
    return res(ctx.json(createInstrumentsResponse([])))
  })

export const getContactInstrumentsSearchableHandler = (
  instruments: ContactInstrument[] = MOCK_INSTRUMENTS
) =>
  rest.get(CONTACT_INSTRUMENTS_URL, (req, res, ctx) => {
    const query = req.url.searchParams.get('q')
    const filtered = query
      ? instruments.filter(i => i.description.toLowerCase().includes(query.toLowerCase()))
      : instruments
    return res(ctx.json(createInstrumentsResponse(filtered)))
  })

export const MOCK_CONTACT_WITH_MISSING_FIELDS: Contact = {
  ...MOCK_CONTACT,
  id: 'missing-fields-contact-1',
  name: 'Incomplete Contact',
  description: 'Contact missing address',
  entity: {
    ...MOCK_CONTACT.entity,
    firstName: 'Incomplete',
    address: {
      addressLine1: '',
      city: '',
      region: '',
      postCode: '',
      country: '',
    },
  },
}

export const getContactsWithMissingFieldsHandler = () =>
  rest.get(`${API_URL}/platform/v1/contacts`, (req, res, ctx) => {
    return res(
      ctx.json({
        ...mockContacts,
        content: [MOCK_CONTACT_WITH_MISSING_FIELDS],
        totalPages: 1,
        totalElements: 1,
        numberOfElements: 1,
        empty: false,
      })
    )
  })

export const MOCK_PAYMENT_INSTRUMENTS: ContactPaymentInstruments[] = [
  MOCK_CRYPTO_PAYMENT_INSTRUMENT_BTC,
  MOCK_CRYPTO_PAYMENT_INSTRUMENT_ETH,
]

export const createPaymentInstrumentsResponse = (
  content: ContactPaymentInstruments[] = MOCK_PAYMENT_INSTRUMENTS
) => ({
  content,
  pageable: {
    pageNumber: 0,
    pageSize: 10,
    sort: { sorted: false, unsorted: true, empty: true },
    offset: 0,
    paged: true,
    unpaged: false,
  },
  totalPages: content.length > 0 ? 1 : 0,
  totalElements: content.length,
  last: true,
  first: true,
  size: 10,
  number: 0,
  sort: { sorted: false, unsorted: true, empty: true },
  numberOfElements: content.length,
  empty: content.length === 0,
})

export const getPaymentInstrumentsHandler = (
  content: ContactPaymentInstruments[] = MOCK_PAYMENT_INSTRUMENTS
) =>
  rest.get(PAYMENT_INSTRUMENTS_URL, (_req, res, ctx) => {
    return res(ctx.json(createPaymentInstrumentsResponse(content)))
  })

export const getPaymentInstrumentsErrorHandler = () =>
  rest.get(PAYMENT_INSTRUMENTS_URL, (_req, res, ctx) => {
    return res(ctx.status(500))
  })

export const getPaymentInstrumentsBtcOnlyHandler = () =>
  rest.get(PAYMENT_INSTRUMENTS_URL, (_req, res, ctx) => {
    return res(ctx.json(createPaymentInstrumentsResponse([MOCK_CRYPTO_PAYMENT_INSTRUMENT_BTC])))
  })

export const getPaymentInstrumentsPendingHandler = () =>
  rest.get(PAYMENT_INSTRUMENTS_URL, (_req, res, ctx) => {
    return res(ctx.delay('infinite'))
  })

export default [getContacts]
